<template>
  <Layout>
    <div class="mc-list-order">
      <div :class="['mc-list-header', { 'mc-list-header--sticky': isStickyHeader }]">
        <PageHeader
          class="mc-list-order__page-header"
          :title="title"
          :sub-title="isMobileHeader ? '' : subtitle"
          :customTitle="isMobileHeader"
          :isSticky="!isMobileHeader || isStickyHeader"
        >
          <template v-if="isMobileHeader" #beforeTitle>
            <McButton
              class="mc-list-order__back-mobile"
              variant="secondary"
              :text="$t('global.back')"
              icon="icon-outline_arrow-left"
              :screenBreakpoint="buttonScreenBreakpoint"
              @click="handleBack"
            />
          </template>
          <template v-if="isMobileHeader" #title>
            <div class="mc-custom-page-title mc-list-order__mobile-title">
              <h4 class="mc-page-title">{{ title }}</h4>
              <p v-if="subtitle">{{ subtitle }}</p>
            </div>
          </template>
          <template v-if="!isMobileHeader" #afterTitle>
            <div class="d-flex gap-2 mc-list-order__header-actions">
              <McButton
                variant="secondary"
                :text="$t('global.back')"
                icon="icon-outline_arrow-left"
                @click="handleBack"
              />
              <McButton
                v-if="isTableDataAvailable"
                variant="primary"
                :text="$t('fleetTask.applyDelivery')"
                icon="icon-outline_arrow-right"
                icon-position="right"
                :disabled="isLoading"
                @click="handleApply"
              />
            </div>
          </template>
          <template v-if="isMobileHeader" #footer>
            <div
              v-if="isTableDataAvailable"
              class="d-flex mc-list-order__header-actions mc-list-order__header-actions--mobile"
            >
              <McButton
                variant="primary"
                class="mc-list-order__apply-mobile"
                :text="$t('fleetTask.applyDelivery')"
                icon="icon-outline_arrow-right"
                icon-position="right"
                :disabled="isLoading"
                @click="handleApply"
              />
            </div>
          </template>
        </PageHeader>
      </div>

      <McAlert
        class="mt-3"
        variant="info"
        :html="$t('fleetTask.attachDelivery.infoAlert')"
        withIcon
        :withGap="false"
      />

      <div v-show="isTableDataAvailable" class="toggle-container">
        <div class="toggle-card">
          <McToggle
            v-model="isTransitAll"
            :label="$t('fleetTask.attachDelivery.markAllTransit')"
            placement="left"
            :compact="true"
            @change="handleTransitAllChange"
          />
          <McButton
            v-if="isTransitAll"
            variant="secondary"
            icon="icon-outline_pin"
            :text="$t('fleetTask.attachDelivery.setTransitLocation')"
            class="ms-2"
            @click="openModalSetLocTransit"
          />
        </div>
      </div>

      <!-- ... existing code ... -->
      <McTable
        v-show="isTableDataAvailable"
        ref="doTable"
        :fields="fields"
        :apiClient="apiClient"
        :customHandlers="{
          handleClickRow: handleClickRow,
        }"
        withCard
        searchable
        stickyHeader
        disableStickyOnMobile
        class="fp-table"
        :activeFilters="activeFilters"
        :disableSelect="isDisableSelect"
        :disableSelectAll="true"
        :hasDelete="false"
        hasViewManager
        freezeColumnBetween
        @handleSelectValue="handleSelectValue"
        @available-change="handleDataAvailable"
        @handlePageChange="handlePageChange"
      >
        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <div class="mc-list-order__view-manager">
            <McFilterSelect
              :defaultColumnOrder="initialFields"
              :currentColumnOrder="dataFields"
              :buttonText="isMobileHeader ? '' : undefined"
              :buttonProps="isMobileHeader ? mobileTableToolbarButtonProps : {}"
              :saveToLocalStorage="false"
              :storageKey="''"
              :entityType="'FleetTaskDeliveryOrder'"
              @setColumn="handleSetAttachDeliveryColumns($event, handleSetDataFields)"
            />
          </div>
        </template>

        <template v-if="!isMobileHeader" #beforeSearchBar>
          <McTableFilter
            ref="tableFilter"
            :activeFilters="activeFilters"
            :parseFilterDataMappings="parseFilterDataMappings"
            :presetFullObjects="presetFullObjects"
            @submit="handleChangeActiveFilters"
            @reset="handleResetFilters"
          >
            <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
              <InputTableFilterDo
                :formValues="formValues"
                :fullObjects="fullObjects"
                :isCreating="isCreating"
                :isSingleProductCategory="isSingleProductCategory"
                :userGroup="userGroupFromParams"
                @update:formValues="updateFormValues($event)"
                @update:fullObjects="updateFullObjects($event)"
              />
            </template>
          </McTableFilter>
        </template>

        <template v-if="isMobileHeader" #afterSearchBar>
          <div class="d-flex gap-2 mc-list-order__search-actions">
            <McTableFilter
              ref="tableFilter"
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              :presetFullObjects="presetFullObjects"
              :buttonProps="mobileTableToolbarButtonProps"
              @submit="handleChangeActiveFilters"
              @reset="handleResetFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilterDo
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  :isCreating="isCreating"
                  :isSingleProductCategory="isSingleProductCategory"
                  :userGroup="userGroupFromParams"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>
          </div>
        </template>

        <template #cell(created_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>

        <template #cell(expected_pickup_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>

        <template #cell(expected_delivered_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>

        <template #cell(line_order)="{ value }">
          {{ value === null || value === undefined || value === '' ? '-' : value }}
        </template>

        <template #cell(transit_shipment)="{ item }">
          <McToggle
            v-model="transitItems[item.id]"
            :label="$t('delivery.status.transit')"
            placement="left"
            @change="(value) => handleSingleTransitChange(item.id, value)"
          />
        </template>

        <template #cell(transit_location)="{ item }">
          <FormulateForm ref="formRef" class="mc-form">
            <FormulateInput
              :ref="`${transitLocationFields.name}-${item.id}-ref`"
              v-bind="{ ...transitLocationFields }"
              v-model="transitAddressValues[item.id]"
              :fullObject.sync="transitAddressObjects[item.id]"
              class="p-0"
              validation-behavior="submit"
              :disabled="!transitItems[item.id]"
            />
          </FormulateForm>
        </template>

        <template #[`cell(status.name)`]="{ value, item }">
          <span
            v-if="item.status?.color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{
            getDeliveryStatusLabel(item.status || { name: value })
          }}</span>
        </template>

        <template #cell(product_categories)="{ value }">
          <McChipList
            v-if="value?.length"
            :items="value"
            :max-visible="3"
            itemTextField="name"
            showAllMode="dropdown"
            :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
            showAllDropdownTrigger="action"
          />
        </template>
      </McTable>

      <b-card v-show="!isTableDataAvailable">
        <McEmptyState
          :title="$t('delivery.dashboard.noData')"
          :desc="$t('fleetTask.emptyState.description')"
          @buttonPrimaryAction="$router.push({ name: 'Delivery Create' })"
        />
      </b-card>

      <b-modal
        id="modal-transit"
        class="schedule-fleet-task__modal"
        :title="$t('fleetTask.attachDelivery.transitModal.title')"
        size="md"
        centered
        scrollable
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
      >
        <div class="card-body p-2">
          <p class="schedule-fleet-task__text">
            {{ $t('fleetTask.attachDelivery.transitModal.description') }}
          </p>
          <FormulateForm
            ref="formRef"
            v-model="formValues"
            class="mc-form mb-2"
            :errors="formErrors"
          >
            <FormulateInput
              :ref="`${transitLocationFields.name}-ref`"
              :label="$t('delivery.fields.transitLocation')"
              v-bind="{ ...transitLocationFields }"
              :fullObject.sync="transitAddressFullObject"
              class="p-0"
              validation-behavior="submit"
              :help="transitLocationFields?.validation?.includes('required')"
            />
          </FormulateForm>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex mx-0">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              :disabled="isLoading"
              @click="cancel()"
            />
            <McButton
              variant="primary"
              :text="$t('global.apply')"
              :disabled="isLoading"
              @click="handleSubmitTransit"
            />
          </div>
        </template>
      </b-modal>

      <b-modal
        id="mixed-product-category-modal"
        class="mc-modal"
        :title="$t('global.confirmation.title')"
        size="md"
        centered
        no-close-on-backdrop
      >
        <div class="card-body">
          <McAlert withIcon class="w-100 mb-3" variant="warning">
            <template #label>
              <span
                ><b>{{ $t('global.alert.text.warningUpperCase') }}!</b>
                {{ $t('fleetTask.attachDelivery.mixedProductCategory.alert') }}</span
              >
            </template>
          </McAlert>
          <p>
            {{ $t('fleetTask.attachDelivery.mixedProductCategory.description') }}
          </p>
          <p>{{ $t('fleetTask.attachDelivery.mixedProductCategory.confirmation') }}</p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton
              variant="primary"
              :text="$t('global.action.continue')"
              @click="
                () => {
                  hasConfirmedMixedProductCategory = true
                  $bvModal.hide('mixed-product-category-modal')
                  handleApply()
                }
              "
            />
          </div>
        </template>
      </b-modal>

      <ModalConfirmSummaryTemperature
        :data="dataConfirmTemp"
        :modal-id="'modal-confirm-summary-temperature-attach'"
        @handleSubmit="handleApplyCreateFO"
      />
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import McToggle from '@/components/McToggle'
import McAlert from '@/components/McAlert'
import McFilterSelect from '@/components/McFilterSelect'
import McTableFilter from '@/components/McTableFilter'
import InputTableFilterDo from './components/InputTableFilterDo'
import ModalConfirmSummaryTemperature from './components/Modal/ConfirmSummaryTemperature'
import McChipList from '@/components/McChipList'

import { fieldsDeliveryOrder } from './lib/fields/attach-delivery-fields'

import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as FleetTaskDeliveryOrder from '@/services/rest-api/lib/fleet-task/delivery-order'
import * as Address from '@/services/rest-api/lib/delivery/address'

import { errorCodeToastMap } from './lib/constants/error-code-mappings'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings-do'
import { statusIdToLabelMap, statusNameToIdMap } from '../delivery/lib/constants/status'

import { mapState } from 'vuex'

import moment from 'moment'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import { TableHelper } from '@/mixin/table'
moment.locale('id')

export default {
  page: {
    title: 'Fleet Task',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McEmptyState,
    McToggle,
    McAlert,
    McFilterSelect,
    McTableFilter,
    InputTableFilterDo,
    ModalConfirmSummaryTemperature,
    McChipList,
  },
  mixins: [Helper, TableHelper],
  beforeRouteLeave(to, from, next) {
    if (to.name === 'Fleet Task Detail') {
      this.$store.dispatch('fleetTask/setIsAttachDelivery', true)
      this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', true)
    }
    next()
  },
  data() {
    return {
      activeFilters: {},
      presetFullObjects: {},
      isTableDataAvailable: true,
      tempSelectedItems: [],
      startedSelectedItems: [],

      isLoading: true,
      isDisableSelect: false,
      transitAddressFullObject: {},
      isTransitAll: false,
      transitItems: {},
      transitAddressObjects: {},
      formValues: {},
      formErrors: {},
      transitAddressValues: {},
      saveToLocalStorage: true,
      storageKey: 'ViewManagerAttachDO',
      localFields: [],
      dataConfirmTemp: {},
      dontShowAgain: false,
      skipCheckTemp: false,
      fleetTaskData: null,
      fleetTaskStorage: null,
      isRecreateFO: false,

      hasConfirmedMixedProductCategory: false,
      originalSelectedItems: [],
      isSingleProductCategory: false,
      isMobileHeader: false,
      hasPageScrolled: false,
    }
  },
  computed: {
    ...mapState('fleetTask', ['selectedDos', 'isLoadingGlobal']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.$t('fleetTask.title.create')
    },
    subtitle() {
      if (!this.isTableDataAvailable) return ''
      return this.$t('fleetTask.subtitle.create')
    },
    isCreating() {
      return this.$route.name === 'Fleet Task Create'
    },
    fields() {
      const visibleFields = this.localFields.filter((f) => {
        if (f.scopes && !this.hasAccessAll(f.scopes)) return false
        return f.isShow !== false
      })

      return this.normalizeLineOrderPosition(visibleFields)
    },
    apiClient() {
      let transitDeliveryOrderIds = []
      if (!this.isCreating && this.tempSelectedItems.length > 0) {
        transitDeliveryOrderIds = this.tempSelectedItems
          .filter((item) => item.as_transit === true)
          .map((item) => item.id)
      }

      const defaultStatuses = [
        'CONFIRMED',
        'RESCHEDULED',
        'SCHEDULED',
        'STARTED',
        'PICKED',
        'IN_TRANSIT',
        'WAITING_FOR_VENDOR',
        'ACCEPTED_BY_VENDOR',
        'REJECTED_BY_VENDOR',
      ]

      let filters = []

      const statusType = this.activeFilters?.status_type
      if (statusType) {
        let statusFilter = []
        if (statusType.all) {
          statusFilter = defaultStatuses
        } else {
          statusFilter = Object.keys(statusType).filter(
            (key) => key !== 'all' && statusType[key] === true
          )
          if (statusFilter.length === 0) statusFilter = defaultStatuses
        }

        filters.push(`status.name in (${statusFilter.join(' ')})`)
      } else {
        filters.push(`status.name in (${defaultStatuses.join(' ')})`)
      }

      filters.push('shipment_type in (UNSCHEDULED TRANSIT)')

      Object.entries(this.activeFilters || {}).forEach(([key, value]) => {
        if (key === 'status_type') return

        if (value !== null && value !== '' && value !== undefined) {
          const [field, operator] = key.split(':')
          if (operator) {
            if (Array.isArray(value)) {
              filters.push(`${field} ${operator} (${value.join(' ')})`)
              return
            }
            filters.push(`${field} ${operator} ${value}`)
          } else {
            filters.push(`${field} ${value}`)
          }
        }
      })

      if (transitDeliveryOrderIds.length > 0) {
        const formattedIds = transitDeliveryOrderIds.map((id) => `${id}`).join(' ')
        filters.push(`id not_in (${formattedIds})`)
      }

      if (this.hasTmsUserGroupAccess && !this.isCreating) {
        const userGroup = this.$route.params.user_group || this.fleetTaskData?.user_group
        const userGroupId = Number(userGroup.id)
        const hasUserGroupFilter = filters.some((f) => f.startsWith('user_group.id'))

        if (!hasUserGroupFilter) {
          filters.push(`user_group.id in (${userGroupId})`)
        }
      }

      if (
        Array.isArray(this.activeFilters['operational_day.id:in']) &&
        this.activeFilters['operational_day.id:in'].length > 0
      ) {
        const daysFilter = this.activeFilters['operational_day.id:in']

        const allDays = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ]

        const selectedDays = daysFilter.includes('all') ? allDays : daysFilter

        filters = filters.filter((f) => !f.startsWith('operational_day.id in'))

        filters.push(`destination_operational_day in (${selectedDays.join(' ')})`)
      }

      if (
        Array.isArray(this.activeFilters['shipped_customer.id:in']) &&
        this.activeFilters['shipped_customer.id:in'].length > 0
      ) {
        filters = filters.filter((f) => !f.startsWith('shipped_customer.id in'))
        filters.push(
          `shipped_customer.id in (${this.activeFilters['shipped_customer.id:in'].join(' ')})`
        )
      }

      if (
        Array.isArray(this.activeFilters['origin_address.id:in']) &&
        this.activeFilters['origin_address.id:in'].length > 0
      ) {
        filters = filters.filter((f) => !f.startsWith('origin_address.id in'))
        filters.push(
          `origin_address.id in (${this.activeFilters['origin_address.id:in'].join(' ')})`
        )
      }

      if (
        Array.isArray(this.activeFilters['destination_address.id:in']) &&
        this.activeFilters['destination_address.id:in'].length > 0
      ) {
        filters = filters.filter((f) => !f.startsWith('destination_address.id in'))
        filters.push(
          `destination_address.id in (${this.activeFilters['destination_address.id:in'].join(' ')})`
        )
      }

      if (
        Array.isArray(this.activeFilters['product_category.id:in']) &&
        this.activeFilters['product_category.id:in'].length > 0
      ) {
        filters = filters.filter((f) => !f.startsWith('product_category.id in'))
        filters.push(
          `product_category.id in (${this.activeFilters['product_category.id:in'].join(' ')})`
        )

        if (!this.isCreating && this.presetFullObjects?.['product_category_id']) {
          filters.push('has_same_product_category eq true')
        }
      }

      return {
        get: Delivery.get(filters),
      }
    },
    isIntervention() {
      return (
        this.$route.query.isIntervention === 'true' || this.$route.query.isIntervention === true
      )
    },
    transitLocationFields() {
      const transitField = {
        name: 'transit_address_id',
        showKey: 'origin_address',
        type: 'select-resourceful-v2',
        optionsGetter: Address.getAddressTransit,
        optionLabelFn: ({ item }) => item.name,
        placeholder: this.$t('delivery.action.selectTransitLocation'),
        validation: 'required',
      }
      return transitField
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read']) && this.isSuperUser()
    },
    userGroupFromParams() {
      if (this.$route.params.user_group) {
        return this.$route.params.user_group
      }

      if (this.fleetTaskData?.user_group) {
        return this.fleetTaskData.user_group
      }
      return null
    },
    hasSingleProductCategoryAccess() {
      return this.hasAccessAll(['fleet-task-single-category-deliveries:read'])
    },
    buttonScreenBreakpoint() {
      return 768
    },
    mobileTableToolbarButtonProps() {
      return {
        screenBreakpoint: 992,
        tooltipPosition: 'bottom',
      }
    },
    isStickyHeader() {
      return this.isMobileHeader && this.hasPageScrolled
    },
    deliveryOrderLimit() {
      return this.increaseDeliveryOrderLimit ? 100 : 50
    },
    increaseDeliveryOrderLimit() {
      return this.getFeatureFlagValue('increaseDeliveryOrderLimitFlag')
    },
    selectedDeliveryOrderCount() {
      return this.isIntervention ? this.startedSelectedItems.length : this.tempSelectedItems.length
    },
  },
  watch: {
    selectedDeliveryOrderCount(newValue) {
      if (newValue < this.deliveryOrderLimit) {
        this.isDisableSelect = false
        return
      }
      this.isDisableSelect = true
    },
    '$refs.doTable.items': {
      handler(newItems) {
        if (newItems && newItems.length) {
          const storeTransitItems = this.$store.state.fleetTask.transitItems || {}

          newItems.forEach((item) => {
            if (storeTransitItems[item.id] !== undefined) {
              this.$set(this.transitItems, item.id, storeTransitItems[item.id])
            } else if (this.transitItems[item.id] === undefined) {
              this.$set(this.transitItems, item.id, this.isTransitAll)
            }
          })
        }
      },
      deep: true,
    },
  },
  async mounted() {
    this.checkHeaderViewport()
    this.handleWindowScroll()
    window.addEventListener('resize', this.checkHeaderViewport)
    window.addEventListener('scroll', this.handleWindowScroll)

    this.localFields = fieldsDeliveryOrder(this)
    this.fleetTaskStorage =
      JSON.parse(sessionStorage.getItem(`fleetTask_${this.$route.params.id}`)) || null
    if (this.saveToLocalStorage) {
      const saved = localStorage.getItem(this.storageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        const companyId = this.getCompanyId()
        const userId = this.getUserId()

        if (
          parsed?.companyId === companyId &&
          parsed?.userId === userId &&
          Array.isArray(parsed.fields)
        ) {
          const freshFields = fieldsDeliveryOrder(this)
          this.localFields = freshFields.map((freshField) => {
            const savedField = parsed.fields.find((f) => f.key === freshField.key)
            if (savedField) {
              return { ...freshField, ...savedField }
            }
            return freshField
          })
        }
      }
    }

    const visibilityMap = {
      'user_group.name': this.hasTmsUserGroupAccess,
    }

    this.localFields.forEach((f) => {
      if (f.key in visibilityMap) {
        f.isShow = visibilityMap[f.key]
      }
    })

    if (
      (!this.isCreating && this.hasTmsUserGroupAccess && !this.$route.params.user_group) ||
      this.hasSingleProductCategoryAccess
    ) {
      await this.fetchFleetTaskData()
    }

    if (this.hasTmsUserGroupAccess && !this.isCreating) {
      const userGroup = this.$route.params.user_group || this.fleetTaskData?.user_group

      if (userGroup?.id) {
        const userGroupId = Number(userGroup.id)
        if (!isNaN(userGroupId)) {
          this.$set(this.activeFilters, 'user_group.id:in', `(${userGroupId})`)
        }
      }
    }

    if (this.hasSingleProductCategoryAccess && !this.isCreating) {
      const productCategory = this.fleetTaskData?.product_category

      if (productCategory?.id) {
        this.isSingleProductCategory = true
        this.$set(this.activeFilters, 'product_category.id:in', [productCategory.id])
        this.$set(this.presetFullObjects, 'product_category_id', [productCategory])
      }
    }

    this.isLoading = true

    const storeTransitItems = this.$store.state.fleetTask.transitItems || {}
    this.transitItems = { ...storeTransitItems }

    if (!this.isCreating) {
      const res = await FleetTaskDeliveryOrder.get(this.$route.params.id)
      if (res.errors) {
        this.isLoading = false
        this.$mcToast.error(this.$t('fleetTask.attachDelivery.errorFetchSelectedDelivery'))
        return
      }

      this.tempSelectedItems = this.isIntervention ? [] : res.data
      this.originalSelectedItems = res.data
      if (this.isIntervention) {
        this.startedSelectedItems = res.data
      }
      this.$store.dispatch('fleetTask/setSelectedDos', this.tempSelectedItems)

      // Wait for Vue to update the computed properties
      this.$nextTick(() => {
        // Call getData on the McTable component to fetch data with the updated apiClient
        if (this.$refs.doTable) {
          this.$refs.doTable.getData({
            limit: this.$refs.doTable.limit,
            page: this.$refs.doTable.page,
            search: this.$refs.doTable.search || undefined,
            sorts: this.$refs.doTable.sorts.map((sort) => `${sort.key} ${sort.order}`),
          })
          this.syncTableSelection()
        }
      })
    }

    this.$store.dispatch('delivery/setStatusTypeFilters', { all: true })
    this.isLoading = false
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkHeaderViewport)
    window.removeEventListener('scroll', this.handleWindowScroll)
    this.$store.dispatch('fleetTask/setTransitAddress', null)
    this.$store.dispatch('fleetTask/setTransitAllState', false)
    this.$store.dispatch('fleetTask/setTransitItems', {})
  },
  methods: {
    formatIndexDate,
    handleWindowScroll() {
      this.hasPageScrolled = window.scrollY > 8
    },
    checkHeaderViewport() {
      this.isMobileHeader = window.innerWidth < this.buttonScreenBreakpoint
    },
    getDeliveryStatusLabel(status) {
      if (!status) return '-'
      if (status.label) return this.$t(status.label)

      const statusId = status.id || statusNameToIdMap[status.name]
      const statusLabel = statusIdToLabelMap[statusId]

      return statusLabel ? this.$t(statusLabel) : status.name
    },
    normalizeLineOrderPosition(fields) {
      const numberIndex = fields.findIndex((field) => field.key === 'number')
      const lineOrderIndex = fields.findIndex((field) => field.key === 'line_order')

      const normalizedFields = this.addStickyColumnByKeys(
        [...fields],
        ['number', 'status.name', 'transit_shipment', 'transit_location']
      )
      if (numberIndex === -1 || lineOrderIndex === -1) {
        return normalizedFields
      }

      const [lineOrderField] = normalizedFields.splice(lineOrderIndex, 1)
      const insertIndex = lineOrderIndex > numberIndex ? numberIndex + 1 : numberIndex
      normalizedFields.splice(insertIndex, 0, lineOrderField)

      return normalizedFields
    },
    handleSetAttachDeliveryColumns(fields, handleSetDataFields) {
      handleSetDataFields(this.normalizeLineOrderPosition(fields))
    },
    async fetchFleetTaskData() {
      if (!this.$route.params.id) return

      const res = await FleetTask.show(this.$route.params.id)
      if (res.errors) {
        return
      }

      this.fleetTaskData = res.data
    },
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable

      if (this.$refs.doTable && this.$refs.doTable.items) {
        this.$refs.doTable.items.forEach((item) => {
          if (!this.transitItems[item.id]) {
            this.$set(this.transitItems, item.id, this.isTransitAll)
          }
        })
      }
    },
    handleClickRow(item) {
      this.$router.push({ name: 'Delivery Detail', params: { id: item.id } })
    },
    async handleApply() {
      if (this.isLoading) return

      try {
        const selectedItemsWithTransit = this.tempSelectedItems.map((item) => ({
          ...item,
          as_transit: item.as_transit ? item.as_transit : this.transitItems[item.id] || false,
          transit_address_id: item.transit_address_id
            ? item.transit_address_id
            : this.transitAddressObjects[item.id]?.id,
        }))

        const invalidTransitItem = selectedItemsWithTransit.find(
          (item) => item.as_transit === true && !item.transit_address_id
        )

        if (invalidTransitItem) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTask.attachDelivery.emptyTransitLocation'),
                },
              ],
            },
          })
          return
        }

        this.$store.dispatch('fleetTask/setSelectedDos', selectedItemsWithTransit)
        this.$store.dispatch('fleetTask/setIsInitial', false)

        const ids = selectedItemsWithTransit.map((v) => v.id)
        if (ids.length === 0 || (this.isRecreateFO && this.$refs.doTable.selectedIdsLength === 0)) {
          this.$mcToast.error(this.$t('fleetTask.chooseDelivery'))
          return
        }

        if (this.hasAccessAll(['tms-cold-chain:read'])) {
          let checkTempData = this.tempSelectedItems
          if (this.isIntervention) {
            checkTempData = this.startedSelectedItems
          }

          const dataTemp = await Delivery.validateDo({
            validation_type: 'HAS_TEMPERATURE_GAP',
            ids: selectedItemsWithTransit.map((item) => item.id),
          })

          const getTemp = dataTemp?.data

          const savedPref = localStorage.getItem('hideTemperatureSummary')

          if (savedPref) {
            this.dontShowAgain = JSON.parse(savedPref)
          }

          if (
            !this.skipCheckTemp &&
            !this.dontShowAgain &&
            getTemp.min !== null &&
            getTemp.max !== null
          ) {
            this.dataConfirmTemp = getTemp
            this.$bvModal.show('modal-confirm-summary-temperature-attach')
            return
          }
        }

        this.isLoading = true
        let id = null

        let productCategoryId = null
        if (this.hasSingleProductCategoryAccess && this.isCreating) {
          if (!this.hasConfirmedMixedProductCategory) {
            const data = await Delivery.validateDo({
              validation_type: 'HAS_MIXED_PRODUCT_CATEGORY',
              ids: selectedItemsWithTransit.map((item) => item.id),
            })

            if (data.result) {
              this.$bvModal.show('mixed-product-category-modal')
              return
            }

            if (data.errors) {
              this.$mcToast.error(this.$t('fleetTask.attachDelivery.requestError'))
              return
            }

            const filteredProductCategory = selectedItemsWithTransit
              .map((item) => item.product_category_ids?.[0])
              .filter(Boolean)
            if (filteredProductCategory.length > 0) {
              productCategoryId = filteredProductCategory[0]
            }
          } else {
            this.hasConfirmedMixedProductCategory = false
          }
        }

        const nonTransitItems = selectedItemsWithTransit
          .filter((item) => !item.as_transit)
          .map((item) => item.id)

        const transitItemsGrouped = selectedItemsWithTransit
          .filter((item) => item.as_transit)
          .reduce((acc, item) => {
            const addressId = item.transit_address_id
            if (!acc[addressId]) {
              acc[addressId] = []
            }
            acc[addressId].push(item.id)
            return acc
          }, {})

        const transitDeliveryOrders = Object.entries(transitItemsGrouped).map(
          ([addressId, ids]) => ({
            delivery_order_ids: ids,
            transit_address_id: parseInt(addressId, 10),
          })
        )

        const payload = {
          delivery_order_ids: nonTransitItems,
          transit_delivery_orders: transitDeliveryOrders,
          product_category_id:
            this.hasSingleProductCategoryAccess && this.isCreating ? productCategoryId : undefined,
        }

        let toastMessage = this.$t('fleetTask.attachDelivery.createAssignmentFailed')
        if (this.isCreating || this.isRecreateFO) {
          const res = await FleetTask.store(payload)

          if (res.errors) {
            const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
            if (issue) toastMessage = errorCodeToastMap[issue.code]

            this.$mcToast.error(toastMessage)

            this.isLoading = false
            return
          }

          id = res.data.id
        } else if (this.isIntervention) {
          const res = await FleetTask.intervention(this.$route.params.id, payload)

          if (res.errors) {
            this.$mcToast.error(this.$t('fleetTask.attachDelivery.addDeliveryFailed'))

            this.isLoading = false
            return
          }

          id = this.$route.params.id
        } else {
          const res = await FleetTaskDeliveryOrder.update(this.$route.params.id, payload)

          if (res.errors) {
            const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
            if (issue) toastMessage = errorCodeToastMap[issue.code]

            this.$mcToast.error(toastMessage)

            this.isLoading = false
            return
          }

          id = this.$route.params.id
        }

        this.$store.dispatch('fleetTask/setSelectedDos', ids)
        this.isLoading = false

        if (!id) return

        let query = {}
        if (this.$route.params.id) {
          query = { ...this.$route.query }
        }

        query.isSetNavsource = false

        this.skipCheckTemp = false

        if (this.isRecreateFO) {
          query.isRecreateFO = this.isRecreateFO
          query.exFO = this.$route.params.id
        }

        this.$router.push({
          name: 'Fleet Task Detail',
          params: { id },
          query,
        })
      } catch (e) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
      } finally {
        this.isLoading = false
      }
    },
    handleBack() {
      this.$store.dispatch('fleetTask/setIsInitial', true)
      const query = { ...this.$route.query }
      const routeName = query.navsource || 'Fleet Task'
      delete query.navsource
      query.isSetNavsource = false

      if (this.isCreating) {
        this.$router.push({ name: routeName, query })
        return
      }

      this.$router.push({ name: 'Fleet Task Detail', id: this.$route.params.id, query })
    },
    handleSelectValue(ids = {}, items = {}) {
      const selectedIds = Object.keys(ids).filter((key) => ids[key] === true)

      const fallbackSelectedItems = [
        ...(this.$refs.doTable?.items || []),
        ...this.tempSelectedItems,
        ...this.startedSelectedItems,
        ...this.originalSelectedItems,
      ].reduce((acc, item) => {
        if (!item?.id) return acc

        acc[String(item.id)] = item
        return acc
      }, {})

      const selectedItems = selectedIds
        .map((id) => {
          const emittedItem = items[id]

          if (emittedItem?.checked) return emittedItem

          return fallbackSelectedItems[id]
        })
        .filter(Boolean)

      const normalizedSelectedItems = selectedIds.map((id) => {
        const matchedItem = selectedItems.find((item) => String(item.id) === id) || {}

        const existingSelectedItem =
          this.tempSelectedItems.find((item) => String(item.id) === id) ||
          this.startedSelectedItems.find((item) => String(item.id) === id) ||
          {}

        return {
          ...existingSelectedItem,
          id: matchedItem?.id ?? existingSelectedItem?.id ?? id,
          as_transit: this.transitItems[id] || existingSelectedItem?.as_transit || false,
          transit_address_id: existingSelectedItem?.transit_address_id ?? null,
          load_min_temperature:
            matchedItem?.load_min_temperature ?? existingSelectedItem?.load_min_temperature ?? null,
          load_max_temperature:
            matchedItem?.load_max_temperature ?? existingSelectedItem?.load_max_temperature ?? null,
          number: matchedItem?.number ?? existingSelectedItem?.number ?? null,
          product_category_ids:
            matchedItem?.product_categories?.map((productCategory) => productCategory?.uuid) ??
            existingSelectedItem?.product_category_ids ??
            matchedItem?.product_category_ids ??
            null,
        }
      })

      const mergedOriginalSelectedItems = [
        ...this.originalSelectedItems.filter(
          (item) =>
            !normalizedSelectedItems.some(
              (selectedItem) => String(selectedItem.id) === String(item.id)
            )
        ),
        ...normalizedSelectedItems,
      ]

      if (this.isIntervention) {
        this.tempSelectedItems = [...normalizedSelectedItems]
      } else {
        this.tempSelectedItems = [...mergedOriginalSelectedItems]
      }
    },
    handleSubmitTransit() {
      if (!this.formValues.transit_address_id) {
        this.formErrors = {
          transit_address_id: [this.$t('global.validation.required')],
        }
        return
      }

      const selectedId = this.formValues.transit_address_id
      const selectedTransitAddress = { ...this.transitAddressFullObject }

      this.$store.dispatch('fleetTask/setTransitAddress', {
        id: selectedId,
        address: selectedTransitAddress,
      })

      if (this.$refs.doTable && this.$refs.doTable.items) {
        this.$refs.doTable.items.forEach((item) => {
          if (this.transitItems[item.id]) {
            this.$set(this.transitAddressObjects, item.id, selectedTransitAddress)
            this.$set(this.transitAddressValues, item.id, selectedId)
          }
        })
      }

      this.$bvModal.hide('modal-transit')
    },
    handleTransitAllChange(value) {
      this.isTransitAll = value

      this.$store.dispatch('fleetTask/setTransitAllState', value)

      let newTransitItems = { ...this.transitItems }

      const allIds = this.$refs.doTable.ids || []

      if (value) {
        allIds.forEach((id) => {
          newTransitItems[id] = true

          if (!this.transitAddressObjects[id]) {
            this.$set(this.transitAddressObjects, id, {})
          }
        })
      } else {
        allIds.forEach((id) => {
          newTransitItems[id] = false
          if (this.transitAddressObjects[id]) {
            this.$set(this.transitAddressObjects, id, {})
          }
        })
        this.$store.dispatch('fleetTask/setTransitAddress', null)
      }

      this.transitItems = newTransitItems
      this.$store.dispatch('fleetTask/setTransitItems', { ...newTransitItems })
    },
    handleSingleTransitChange(itemId, value) {
      this.$set(this.transitItems, itemId, value)

      if (!this.transitAddressObjects[itemId]) {
        this.$set(this.transitAddressObjects, itemId, {})
      }

      if (!value) {
        this.$set(this.transitAddressObjects, itemId, {})
      }

      this.$store.dispatch('fleetTask/setTransitItems', { ...this.transitItems })

      this.checkAllTransitItems()
    },
    checkAllTransitItems() {
      const allIds = this.$refs.doTable.ids || []

      if (allIds.length === 0) return false

      const allActive = allIds.every((id) => this.transitItems[id] === true)

      if (this.isTransitAll !== allActive) {
        this.isTransitAll = allActive
        this.$store.dispatch('fleetTask/setTransitAllState', allActive)
      }

      return allActive
    },
    openModalSetLocTransit() {
      this.formErrors = {}

      const transitAddress = this.$store.state.fleetTask.transitAddress
      if (transitAddress && transitAddress.id) {
        this.formValues.transit_address_id = transitAddress.id
        this.transitAddressFullObject = transitAddress.address
      }

      this.$bvModal.show('modal-transit')
    },

    handlePageChange() {
      const transitAddress = this.$store.state.fleetTask.transitAddress
      const storeTransitItems = this.$store.state.fleetTask.transitItems || {}
      const isTransitAll = this.$store.state.fleetTask.isTransitAll || false

      if (this.$refs.doTable && this.$refs.doTable.items) {
        this.$refs.doTable.items.forEach((item) => {
          if (isTransitAll) {
            this.$set(this.transitItems, item.id, true)
          } else if (storeTransitItems[item.id] !== undefined) {
            this.$set(this.transitItems, item.id, storeTransitItems[item.id])
          } else if (this.transitItems[item.id] === undefined) {
            this.$set(this.transitItems, item.id, false)
          }

          if (this.transitItems[item.id] && transitAddress && transitAddress.id) {
            this.$set(this.transitAddressObjects, item.id, transitAddress.address)
            this.$set(this.transitAddressValues, item.id, transitAddress.id)
          }
        })
      }

      if (transitAddress && transitAddress.id) {
        const refName = `${this.transitLocationFields.name}-ref`
        if (this.$refs[refName]) {
          this.formValues.transit_address_id = transitAddress.id
          this.transitAddressFullObject = transitAddress.address
        }
      }
    },
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        // selalu hapus dulu
        this.$delete(this.activeFilters, filter.key)

        const v = filter.values

        if (v === null || v === undefined || v === '') return

        if (Array.isArray(v) && v.length === 0) return

        if (
          Array.isArray(v) &&
          v.every((item) => item === null || item === undefined || item === '')
        )
          return

        let value = v

        if (filter.key === 'user_group.id:in' && Array.isArray(v)) {
          value = `(${v.join(' ')})`
        } else if (filter.key === 'user_group.id:in' && !Array.isArray(v)) {
          value = `(${v})`
          if (filter.key === 'user_group.id:in' && !this.isCreating) {
            value = v
          }
        }

        this.$set(this.activeFilters, filter.key, value)
      })
    },
    handleResetFilters() {
      this.$store.dispatch('delivery/setStatusTypeFilters', { all: true })
    },
    handleApplyCreateFO() {
      this.skipCheckTemp = true
      this.handleApply()
    },
    syncTableSelection() {
      this.$nextTick(() => {
        if (
          this.fleetTaskStorage?.status &&
          [this.$t('fleetTask.status.draft'), this.$t('fleetTask.status.scheduled')].includes(
            this.fleetTaskStorage.status
          )
        )
          return
        if (!this.$refs.doTable) return

        const selectedIds = this.tempSelectedItems.reduce((acc, item) => {
          acc[item.id] = true
          return acc
        }, {})

        this.$set(this.$refs.doTable, 'selectedIds', { ...selectedIds })
        this.$set(this.$refs.doTable, 'selectedIdsLength', Object.keys(selectedIds).length)
      })
    },
  },
}
</script>
<style lang="scss">
.mc-list-order {
  .mc-list-header {
    margin-bottom: 24px;
  }

  .mc-list-order__header-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .mc-list-order__header-actions--mobile {
    width: 100%;
    margin-top: 12px;
  }

  .mc-list-order__apply-mobile {
    width: 100%;
  }

  .mc-list-order__search-actions {
    flex: 0 0 auto;
  }

  .mc-list-order__back-mobile {
    margin-right: 10px;
  }

  .mc-list-order__mobile-title {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
    padding: 0;
  }

  .mc-list-order__mobile-title .mc-page-title {
    margin-bottom: 0;
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .mc-set-location-transit {
    &.has-selected-items {
      margin-left: 24px;
    }
  }

  .mc-toggle-transit {
    top: 3px;
  }

  .mc-toggle-transit--active {
    top: 8px;
  }

  .toggle-container {
    margin-top: 10px;
    margin-bottom: 10px;
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .toggle-card {
    display: flex;
    align-items: center;
    padding: 5px 10px;
    background: #ffffff;
    border: 1px solid #e6e7ec;
    border-radius: 6px;
    min-height: 48px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 65px;
  }

  .mc-list-order__page-header {
    .banner-header {
      padding: 0px;
      margin: 0;
    }
  }

  @media screen and (max-width: 767px) {
    .mc-list-header {
      margin-bottom: 16px;
    }

    .mc-list-header--sticky {
      position: sticky;
      top: 70px;
      z-index: 116;
      width: auto;
      margin-left: -18px;
      margin-right: -18px;
      box-sizing: border-box;

      .banner-header {
        margin: 0;
        padding: 12px 18px 16px;
        background: $white;
        border-bottom: 1px solid #edf2f7;
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
      }
    }

    .mc-list-order__header-actions {
      justify-content: flex-start;
      flex-wrap: nowrap;
    }

    :deep(.mc-list-order__page-header .row > .col-12) {
      align-items: flex-start !important;
    }

    :deep(.mc-list-order__page-header .row > .col-12 > .d-flex:first-child) {
      width: 100%;
      align-items: flex-start;
      gap: 12px;
    }

    .mc-list-order__mobile-title {
      padding-top: 2px;
    }

    .mc-list-order__mobile-title p {
      margin: 0;
      font-size: 14px;
      line-height: 1.45;
      color: $mep-color-text-400;
    }

    .toggle-container {
      margin-top: 12px;
      margin-bottom: 12px;
    }

    .toggle-card {
      width: 100%;
      min-height: 0;
      padding: 12px;
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }

    .toggle-card .ms-2 {
      margin-left: 0 !important;
    }

    .toggle-card .mc-button-wrapper,
    .toggle-card .mc-button {
      width: 100%;
    }

    .toggle-card .mc-button {
      justify-content: center;
    }

    :deep(.slot-item-end) {
      width: 100%;
      justify-content: flex-start !important;
      align-items: center;
      flex-wrap: nowrap;
    }

    :deep(.mc-has-search) {
      flex: 1 1 0;
      min-width: 0;
      order: 1;
    }

    .mc-list-order__view-manager {
      order: 2;
      flex: 0 0 auto;
    }

    .mc-list-order__search-actions {
      order: 3;
      flex: 0 0 auto;
      align-items: center;
    }

    :deep(.mc-list-order__search-actions .mc-button),
    :deep(.mc-list-order__view-manager .mc-button) {
      padding-left: 8px;
      padding-right: 8px;
      min-width: 40px;
      justify-content: center;
    }

    :deep(.mc-list-order__view-manager .mc-button-wrapper) {
      width: auto;
    }

    :deep(.mc-list-order__view-manager .mc-button) {
      width: auto;
    }

    .mc-list-order {
      .mc-table-footer {
        flex-direction: column;
        align-items: center;
        gap: 8px;
      }

      .mc-pagination-container {
        justify-content: center;
      }
    }
  }
}
</style>
